<template>
  <div class="sm-main-container">
    <div v-if="!selectedMonthId" class="sm-dashboard">
      <!-- Header -->
      <div class="sm-header">
        <div class="sm-header-content">
          <div class="d-flex align-center flex-grow-1" style="gap: 16px">
            <!-- Studio Selector (nur wenn mehrere Studios) -->
            <v-select
              v-if="availableStudios.length > 1"
              filled
              dense
              dark
              hide-details
              :items="availableStudios"
              v-model="selectedStudio"
              item-text="name"
              item-value="_id"
              @change="fetchMonthlyPlans"
              class="sm-studio-select"
              prepend-inner-icon="mdi-domain"
              style="max-width: 280px">
            </v-select>
            <div v-else-if="availableStudios.length === 1" class="d-flex align-center">
              <v-icon small color="rgba(255,255,255,0.5)" class="mr-2">mdi-domain</v-icon>
              <span class="text-subtitle-1 font-weight-medium white--text" style="opacity: 0.8">{{ availableStudios[0].name }}</span>
            </div>
          </div>
          <v-btn color="white" class="sm-create-btn" @click="openDialog('create', $event)" depressed>
            <v-icon left small>mdi-plus</v-icon>
            Neuen Monat planen
          </v-btn>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="sm-loading">
        <v-progress-circular indeterminate color="green accent-3" size="56" width="3"></v-progress-circular>
        <p class="mt-4 text-body-2" style="color: rgba(255, 255, 255, 0.5)">Lade Übersicht…</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="monthlyPlans.length === 0" class="sm-empty">
        <div class="sm-empty-card">
          <div class="sm-empty-icon-wrap">
            <v-icon size="48" color="rgba(255,255,255,0.2)">mdi-calendar-blank-outline</v-icon>
          </div>
          <h3 style="color: rgba(255, 255, 255, 0.6); font-weight: 500">Noch keine Planungen</h3>
          <p style="color: rgba(255, 255, 255, 0.35); max-width: 300px" class="text-body-2 mt-1">
            Erstelle den ersten Arbeitsplan und starte mit der Schichtplanung.
          </p>
          <v-btn color="green accent-3" class="mt-5 black--text font-weight-bold" depressed @click="openDialog('create', $event)">
            <v-icon left small>mdi-plus</v-icon>
            Jetzt planen
          </v-btn>
        </div>
      </div>

      <!-- Month Cards Grid -->
      <div v-else class="sm-grid">
        <div
          v-for="plan in formattedPlans"
          :key="plan._id"
          class="sm-month-card"
          :class="{ 'sm-month-active': plan.isActive }"
          @click="openMonthDetail(plan._id)">
          <!-- Card Glow for active -->
          <div v-if="plan.isActive" class="sm-card-glow"></div>

          <div class="sm-card-inner">
            <!-- Top Row -->
            <div class="d-flex justify-space-between align-center mb-3">
              <div class="d-flex align-center" style="gap: 10px">
                <div class="sm-month-icon" :class="plan.isActive ? 'active' : plan.isFuture ? 'future' : 'past'">
                  <v-icon small color="white">{{
                    plan.isActive ? "mdi-play-circle" : plan.isFuture ? "mdi-clock-outline" : "mdi-check-circle"
                  }}</v-icon>
                </div>
                <div>
                  <div class="text-subtitle-1 font-weight-bold white--text">{{ plan.monthName }}</div>
                  <div
                    class="text-caption"
                    :style="{ color: plan.publicationStatus === 'draft' ? '#ffb300' : 'rgba(255, 255, 255, 0.4)', marginTop: '-2px' }">
                    {{
                      plan.publicationStatus === "draft"
                        ? "Entwurf · Team sieht ihn nicht"
                        : plan.isActive
                        ? "Aktueller Monat · freigegeben"
                        : plan.isFuture
                        ? "Freigegeben"
                        : "Abgeschlossen"
                    }}
                  </div>
                </div>
              </div>
              <v-icon small style="color: rgba(255, 255, 255, 0.2)">mdi-chevron-right</v-icon>
            </div>

            <!-- Stats -->
            <div class="sm-stats-row">
              <div class="sm-stat">
                <div class="sm-stat-value">{{ plan.assignedShifts }}</div>
                <div class="sm-stat-label">Besetzt</div>
              </div>
              <div class="sm-stat-divider"></div>
              <div class="sm-stat">
                <div class="sm-stat-value">{{ plan.totalShifts }}</div>
                <div class="sm-stat-label">Gesamt</div>
              </div>
              <div class="sm-stat-divider"></div>
              <div class="sm-stat">
                <div class="sm-stat-value" :class="plan.unassignedShifts > 0 ? 'warning-val' : 'success-val'">
                  {{ plan.unassignedShifts }}
                </div>
                <div class="sm-stat-label">Offen</div>
              </div>
            </div>

            <!-- Progress -->
            <div class="sm-progress-wrap">
              <div class="sm-progress-bg">
                <div
                  class="sm-progress-fill"
                  :class="plan.assignedShifts === plan.totalShifts ? 'complete' : 'partial'"
                  :style="{ width: (plan.assignedShifts / Math.max(plan.totalShifts, 1)) * 100 + '%' }"></div>
              </div>
              <div
                class="text-caption font-weight-bold"
                :class="plan.assignedShifts === plan.totalShifts ? 'success-val' : 'warning-val'"
                style="min-width: 36px; text-align: right">
                {{ Math.round((plan.assignedShifts / Math.max(plan.totalShifts, 1)) * 100) }}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Month Fullscreen Dialog -->
    <v-dialog dark :value="!!selectedMonthId" @input="onDialogInput" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card dark class="rounded-0 elevation-0" style="height: 100vh; overflow-y: auto; background: #121212">
        <shift-management-month-detail v-if="selectedMonthId" :studio-id="selectedStudio" :month-id="selectedMonthId" @back="closeMonthDetail" />
      </v-card>
    </v-dialog>

    <!-- Dialog-Komponente (mit Studio-Prop) -->
    <shift-management-main-dialog
      v-model="dialog"
      :mode="dialogMode"
      :origin="dialogOrigin"
      :pre-selected-studio="selectedStudio"
      :existing-plans="monthlyPlans"
      :existing-plans-studio-id="monthlyPlansStudioId"
      @open-existing-month="handleOpenExistingMonth"
      @input="handleDialogClosed" />
  </div>
</template>

<script>
import shiftManagementMainDialog from "./shiftManagementMainDialog.vue"
import shiftManagementMonthDetail from "./shiftManagementMonthDetail.vue"
import api from "@/api"

export default {
  name: "shiftManagementMain",
  components: {
    shiftManagementMainDialog,
    shiftManagementMonthDetail,
  },
  data() {
    return {
      loading: true,
      availableStudios: [],
      selectedStudio: null,
      monthlyPlans: [],
      monthlyPlansStudioId: null,
      selectedMonthId: null,
      dialog: false,
      dialogMode: "create",
      dialogOrigin: "center center",
    }
  },
  computed: {
    formattedPlans() {
      if (!this.monthlyPlans) return []
      const currentMonth = this.$moment().format("YYYY-MM")

      return this.monthlyPlans.map((plan) => {
        const momentDate = this.$moment(plan._id, "YYYY-MM")
        return {
          ...plan,
          monthName: momentDate.format("MMMM YYYY"),
          isFuture: plan._id > currentMonth,
          isActive: plan._id === currentMonth,
          isPast: plan._id < currentMonth,
        }
      })
    },
  },
  async created() {
    try {
      this.availableStudios = await api.fetchAllowedAdminStudios()
      if (this.availableStudios.length > 0) {
        this.selectedStudio = this.availableStudios[0]._id
        await this.fetchMonthlyPlans()
      }
    } catch (e) {
      console.error(e)
      this.$toast.error("Fehler beim Laden der Studios.")
    } finally {
      this.loading = false
    }
  },
  methods: {
    async fetchMonthlyPlans() {
      if (!this.selectedStudio) return
      const requestedStudioId = String(this.selectedStudio)
      if (String(this.monthlyPlansStudioId || "") !== requestedStudioId) {
        this.monthlyPlans = []
        this.monthlyPlansStudioId = null
      }
      this.loading = true
      try {
        const plans = await api.shiftManagement.fetchPlansOverview(requestedStudioId)
        if (String(this.selectedStudio) !== requestedStudioId) return
        this.monthlyPlans = plans
        this.monthlyPlansStudioId = requestedStudioId
      } catch (e) {
        if (String(this.selectedStudio) !== requestedStudioId) return
        console.error(e)
        this.$toast.error("Fehler beim Laden der Monatsübersicht.")
      } finally {
        if (String(this.selectedStudio) === requestedStudioId) this.loading = false
      }
    },
    openDialog(mode, event) {
      this.dialogMode = mode
      const rect = event.currentTarget.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      this.dialogOrigin = `${centerX}px ${centerY}px`
      this.dialog = true
    },
    handleDialogClosed(val) {
      if (!val) {
        setTimeout(() => {
          if (!this.selectedMonthId) this.fetchMonthlyPlans()
        }, 500)
      }
    },
    openMonthDetail(monthId) {
      this.selectedMonthId = monthId
    },
    handleOpenExistingMonth({ studioId, monthId } = {}) {
      if (!studioId || !monthId) return
      this.dialog = false
      this.selectedStudio = studioId
      this.$nextTick(() => {
        this.selectedMonthId = monthId
      })
    },
    closeMonthDetail() {
      this.selectedMonthId = null
      this.fetchMonthlyPlans()
    },
    onDialogInput(val) {
      if (!val) {
        this.closeMonthDetail()
      }
    },
  },
}
</script>

<style scoped>
.sm-dashboard {
  min-height: 100%;
  padding: 0 24px 24px;
}

/* Header */
.sm-header {
  padding: 8px 0 20px;
}
.sm-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.sm-studio-select {
  border-radius: 10px !important;
}
.sm-studio-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.06) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 10px !important;
}
.sm-create-btn {
  background: linear-gradient(135deg, #4caf50, #2e7d32) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  font-weight: 600 !important;
  padding: 0 20px !important;
  height: 40px !important;
  box-shadow: 0 4px 20px rgba(76, 175, 80, 0.3) !important;
  transition: all 0.3s ease !important;
}
.sm-create-btn:hover {
  box-shadow: 0 6px 28px rgba(76, 175, 80, 0.5) !important;
  transform: translateY(-1px);
}

/* Loading */
.sm-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 120px;
}

/* Empty */
.sm-empty {
  display: flex;
  justify-content: center;
  padding-top: 80px;
}
.sm-empty-card {
  text-align: center;
  padding: 48px 40px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px dashed rgba(255, 255, 255, 0.1);
}
.sm-empty-icon-wrap {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

/* Grid */
.sm-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

/* Month Card */
.sm-month-card {
  position: relative;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}
.sm-month-card:hover {
  transform: translateY(-3px);
}
.sm-month-card:hover .sm-card-inner {
  border-color: rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
}
.sm-card-glow {
  position: absolute;
  inset: -1px;
  border-radius: 17px;
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.3), rgba(76, 175, 80, 0.05));
  z-index: 0;
}
.sm-card-inner {
  position: relative;
  z-index: 1;
  padding: 20px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}
.sm-month-active .sm-card-inner {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(76, 175, 80, 0.3);
}

/* Month Icon */
.sm-month-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sm-month-icon.active {
  background: linear-gradient(135deg, #4caf50, #2e7d32);
}
.sm-month-icon.future {
  background: linear-gradient(135deg, #2196f3, #1565c0);
}
.sm-month-icon.past {
  background: rgba(255, 255, 255, 0.08);
}

/* Stats */
.sm-stats-row {
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 12px 0;
  margin: 4px 0 12px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.2);
}
.sm-stat {
  text-align: center;
}
.sm-stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}
.sm-stat-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 2px;
}
.sm-stat-divider {
  width: 1px;
  height: 28px;
  background: rgba(255, 255, 255, 0.08);
}
.warning-val {
  color: #ffa726 !important;
}
.success-val {
  color: #66bb6a !important;
}

/* Progress */
.sm-progress-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}
.sm-progress-bg {
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}
.sm-progress-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}
.sm-progress-fill.complete {
  background: linear-gradient(90deg, #43a047, #66bb6a);
}
.sm-progress-fill.partial {
  background: linear-gradient(90deg, #fb8c00, #ffa726);
}

/* LIGHT MODE STYLES OVERRIDES */
.sm-dashboard.light-mode {
  background: #f5f5f7;
  color: #1d1d1f;
}
.sm-dashboard.light-mode .sm-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.sm-dashboard.light-mode .sm-header-content span {
  color: #1d1d1f !important;
}
.sm-dashboard.light-mode .sm-studio-select >>> .v-input__slot {
  background: #ffffff !important;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}
.sm-dashboard.light-mode .sm-month-card {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}
.sm-dashboard.light-mode .sm-card-inner {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.sm-dashboard.light-mode .sm-card-inner:hover {
  border-color: rgba(0, 0, 0, 0.15);
  background: #fafafa;
}
.sm-dashboard.light-mode .white--text {
  color: #1d1d1f !important;
}
.sm-dashboard.light-mode .sm-month-icon.past {
  background: rgba(0, 0, 0, 0.06);
}
.sm-dashboard.light-mode .sm-month-icon.past .v-icon {
  color: rgba(0, 0, 0, 0.45) !important;
}
.sm-dashboard.light-mode .sm-stats-row {
  background: #f5f5f7;
}
.sm-dashboard.light-mode .sm-stat-value {
  color: #1d1d1f;
}
.sm-dashboard.light-mode .sm-stat-label {
  color: rgba(0, 0, 0, 0.45);
}
.sm-dashboard.light-mode .sm-stat-divider {
  background: rgba(0, 0, 0, 0.08);
}
.sm-dashboard.light-mode .sm-progress-bg {
  background: rgba(0, 0, 0, 0.08);
}
.sm-dashboard.light-mode .sm-empty-card {
  background: #ffffff;
  border: 1px dashed rgba(0, 0, 0, 0.15);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.02);
}
.sm-dashboard.light-mode .sm-empty-card h3 {
  color: rgba(0, 0, 0, 0.7) !important;
}
.sm-dashboard.light-mode .sm-empty-card p {
  color: rgba(0, 0, 0, 0.5) !important;
}
.sm-dashboard.light-mode .sm-empty-icon-wrap {
  background: rgba(0, 0, 0, 0.04);
}
.sm-dashboard.light-mode .sm-empty-icon-wrap .v-icon {
  color: rgba(0, 0, 0, 0.3) !important;
}
</style>
